/** Minimal markdown → HTML for public help/trust docs (no extra deps). */

function escapeHtml(text: string): string {
	return text
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;");
}

function inlineMarkdown(text: string): string {
	let out = escapeHtml(text);
	out = out.replace(/`([^`]+)`/g, "<code>$1</code>");
	out = out.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
	out = out.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_m, label: string, href: string) => {
		const safeHref = href.startsWith("http") || href.startsWith("/") || href.startsWith("mailto:")
			? href
			: "#";
		return `<a href="${escapeHtml(safeHref)}" class="text-wi-gold underline-offset-2 hover:underline">${label}</a>`;
	});
	return out;
}

export function docRelativePathToRoute(relativePath: string): string | null {
	const withExt = relativePath.replace(/\.md$/, "");
	const stripped = withExt.replace(/\/README$/, "");
	const map: Record<string, string> = {
		"teams/README": "/help",
		"teams/getting-started": "/help/getting-started",
		"teams/running-a-session": "/help/running-a-session",
		"teams/consensus-and-charts": "/help/consensus-and-charts",
		"teams/using-in-slack": "/help/slack",
		"teams/using-in-jira": "/help/jira",
		"teams/using-in-clickup": "/help/clickup",
		"teams/using-in-asana": "/help/asana",
		"teams/using-in-teams": "/help/teams",
		"admin/faq": "/help/admin",
		"admin/troubleshooting": "/help/troubleshooting",
		"admin/uat-install/README": "/help/admin/uat-install",
		"admin/uat-install/slack": "/help/admin/uat-install/slack",
		"admin/uat-install/jira": "/help/admin/uat-install/jira",
		"admin/uat-install/clickup": "/help/admin/uat-install/clickup",
		"admin/uat-install/asana": "/help/admin/uat-install/asana",
		"admin/uat-install/web": "/help/admin/uat-install/web",
		"platform-owners/README": "/help/install",
		"platform-owners/web": "/help/install/web",
		"platform-owners/slack": "/help/install/slack",
		"platform-owners/jira": "/help/install/jira",
		"platform-owners/clickup": "/help/install/clickup",
		"platform-owners/asana": "/help/install/asana",
		"platform-owners/microsoft-teams": "/help/install/microsoft-teams",
		"trust/security-overview": "/trust/security",
		"trust/privacy-policy-summary": "/trust/privacy",
		"trust/data-processing": "/trust/data-processing",
		"trust/compliance": "/trust/compliance",
		"trust/vulnerability-disclosure": "/trust/vulnerability-disclosure",
	};
	return map[withExt] ?? map[stripped] ?? null;
}

export function rewriteDocLink(href: string, currentDocPath: string): string {
	if (href.startsWith("http") || href.startsWith("mailto:") || href.startsWith("#")) return href;
	if (href.startsWith("/")) return href;

	const baseDir = currentDocPath.includes("/") ? currentDocPath.replace(/\/[^/]+$/, "") : "";
	const normalized = href
		.replace(/^\.\//, "")
		.split("/")
		.reduce<string[]>((acc, part) => {
			if (part === "..") acc.pop();
			else if (part !== ".") acc.push(part);
			return acc;
		}, baseDir ? baseDir.split("/") : [])
		.join("/");

	return docRelativePathToRoute(normalized) ?? href;
}

function parseTableRow(line: string): string[] {
	const trimmed = line.trim();
	const inner = trimmed.startsWith("|") ? trimmed.slice(1) : trimmed;
	const withoutTrail = inner.endsWith("|") ? inner.slice(0, -1) : inner;
	return withoutTrail.split("|").map((c) => c.trim());
}

function isTableSeparator(line: string): boolean {
	const cells = parseTableRow(line);
	return cells.length > 0 && cells.every((c) => /^:?-+:?$/.test(c));
}

function slugifyHeading(text: string): string {
	return text
		.toLowerCase()
		.replace(/[`*_~]/g, "")
		.replace(/[^a-z0-9\s-]/g, "")
		.trim()
		.replace(/\s+/g, "-")
		.replace(/-+/g, "-");
}

function rewriteInlineLinks(text: string, currentDocPath: string): string {
	return text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_m, label: string, href: string) => {
		const resolved = rewriteDocLink(href, currentDocPath);
		return `[${label}](${resolved})`;
	});
}

export function renderMarkdown(source: string, currentDocPath: string): string {
	const lines = source.replace(/\r\n/g, "\n").split("\n");
	const html: string[] = [];
	let inCode = false;
	let codeBuf: string[] = [];
	let listOpen: "ul" | "ol" | null = null;
	let i = 0;
	/** PublicDocLayout already provides the hub H1 — demote MD # to a page title. */
	const demoteDocH1 = currentDocPath.startsWith("teams/") || currentDocPath.startsWith("admin/") || currentDocPath.startsWith("platform-owners/") || currentDocPath.startsWith("trust/");

	const closeList = () => {
		if (listOpen) {
			html.push(`</${listOpen}>`);
			listOpen = null;
		}
	};

	while (i < lines.length) {
		const raw = lines[i]!;
		const line = raw.trimEnd();

		if (line.startsWith("```")) {
			if (inCode) {
				html.push(
					`<pre class="overflow-x-auto rounded-lg border border-wi-text/10 bg-wi-bg/80 p-4 text-sm"><code>${escapeHtml(codeBuf.join("\n"))}</code></pre>`,
				);
				codeBuf = [];
				inCode = false;
			} else {
				closeList();
				inCode = true;
			}
			i += 1;
			continue;
		}
		if (inCode) {
			codeBuf.push(raw);
			i += 1;
			continue;
		}

		if (!line.trim()) {
			closeList();
			i += 1;
			continue;
		}

		if (line.startsWith("# ")) {
			closeList();
			const title = line.slice(2);
			const id = slugifyHeading(title.replace(/\*\*/g, ""));
			if (demoteDocH1) {
				html.push(
					`<h2 id="${id}" class="font-display text-h1 text-wi-text scroll-mt-8">${inlineMarkdown(title)}</h2>`,
				);
			} else {
				html.push(
					`<h1 id="${id}" class="font-display text-3xl text-wi-text md:text-4xl scroll-mt-8">${inlineMarkdown(title)}</h1>`,
				);
			}
			i += 1;
			continue;
		}
		if (line.startsWith("## ")) {
			closeList();
			const title = line.slice(3);
			const id = slugifyHeading(title.replace(/\*\*/g, ""));
			html.push(
				`<h2 id="${id}" class="mt-lg font-display text-h2 text-wi-text scroll-mt-8">${inlineMarkdown(title)}</h2>`,
			);
			i += 1;
			continue;
		}
		if (line.startsWith("### ")) {
			closeList();
			const title = line.slice(4);
			const id = slugifyHeading(title.replace(/\*\*/g, ""));
			html.push(
				`<h3 id="${id}" class="mt-md font-medium text-lg text-wi-text scroll-mt-8">${inlineMarkdown(title)}</h3>`,
			);
			i += 1;
			continue;
		}
		if (line.startsWith("> ")) {
			closeList();
			const quoteLines: string[] = [];
			while (i < lines.length && lines[i]!.trimEnd().startsWith("> ")) {
				quoteLines.push(lines[i]!.trimEnd().slice(2));
				i += 1;
			}
			const body = quoteLines.map((q) => inlineMarkdown(rewriteInlineLinks(q, currentDocPath))).join("<br />");
			html.push(
				`<blockquote class="rounded-lg border border-wi-gold/25 bg-wi-gold/6 px-4 py-3 text-sm text-wi-text/85 leading-relaxed">${body}</blockquote>`,
			);
			continue;
		}
		if (line.trim().startsWith("|") && i + 1 < lines.length && isTableSeparator(lines[i + 1]!.trimEnd())) {
			closeList();
			const header = parseTableRow(line);
			i += 2; // skip header + separator
			const rows: string[][] = [];
			while (i < lines.length && lines[i]!.trim().startsWith("|") && !isTableSeparator(lines[i]!.trimEnd())) {
				rows.push(parseTableRow(lines[i]!.trimEnd()));
				i += 1;
			}
			const th = header
				.map(
					(c) =>
						`<th class="border-b border-wi-text/15 px-3 py-2 text-left font-medium text-wi-text">${inlineMarkdown(rewriteInlineLinks(c, currentDocPath))}</th>`,
				)
				.join("");
			const tr = rows
				.map((row) => {
					const tds = row
						.map(
							(c) =>
								`<td class="border-b border-wi-text/10 px-3 py-2 align-top text-wi-text/85">${inlineMarkdown(rewriteInlineLinks(c, currentDocPath))}</td>`,
						)
						.join("");
					return `<tr>${tds}</tr>`;
				})
				.join("");
			html.push(
				`<div class="mt-4 overflow-x-auto rounded-lg border border-wi-text/10"><table class="w-full min-w-[20rem] border-collapse text-sm"><thead><tr>${th}</tr></thead><tbody>${tr}</tbody></table></div>`,
			);
			continue;
		}
		if (/^[-*] /.test(line)) {
			if (listOpen !== "ul") {
				closeList();
				html.push('<ul class="list-disc space-y-2 pl-5 text-sm text-wi-text/85">');
				listOpen = "ul";
			}
			const item = line.replace(/^[-*] /, "");
			html.push(`<li>${inlineMarkdown(rewriteInlineLinks(item, currentDocPath))}</li>`);
			i += 1;
			continue;
		}
		if (/^\d+\. /.test(line)) {
			if (listOpen !== "ol") {
				closeList();
				html.push('<ol class="list-decimal space-y-2 pl-5 text-sm text-wi-text/85">');
				listOpen = "ol";
			}
			const item = line.replace(/^\d+\. /, "");
			html.push(`<li>${inlineMarkdown(rewriteInlineLinks(item, currentDocPath))}</li>`);
			i += 1;
			continue;
		}

		// Horizontal rule
		if (/^---+$/.test(line.trim())) {
			closeList();
			html.push('<hr class="my-8 border-wi-text/10" />');
			i += 1;
			continue;
		}

		closeList();
		html.push(
			`<p class="text-sm text-wi-text/85 leading-relaxed">${inlineMarkdown(rewriteInlineLinks(line, currentDocPath))}</p>`,
		);
		i += 1;
	}

	closeList();
	if (inCode && codeBuf.length) {
		html.push(
			`<pre class="overflow-x-auto rounded-lg border border-wi-text/10 bg-wi-bg/80 p-4 text-sm"><code>${escapeHtml(codeBuf.join("\n"))}</code></pre>`,
		);
	}

	return html.join("\n");
}
